import i18next from 'i18next';
import { throttle } from 'lodash';
import React, { ReactElement, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setTopError } from 'slice/errorSlice';

import { refetchAllRooms, updateRooms } from '../slice/chatGroupSlice';
import { selectVoiceCall, updateMembers } from '../slice/voiceCallSlice';
import { increaseRetry, selectWebsocket, startWebsocket } from '../slice/websocketSlice';

const MAX_RETRY = 10;

// websocket object isn't able to store in store because "A non-serializable value was detected in the state." error occurs.
// Instead of defining in store, define as global variable
let SOCKET: WebSocket | null = null;

// refetchAllRoomsとgetFriendsはサーバーへの負荷がかかるのでthrottleで制限をかける
// https://github.com/ReactCorp/Cocalero_mvp/issues/1555
// https://gist.github.com/krstffr/245fe83885b597aabaf06348220c2fe9
const throttledRefetchAllRooms = throttle((dispatch) => dispatch(refetchAllRooms()), 1000);

export const WebsocketProvider: React.FC<{ children: ReactElement }> = (props) => {
    const dispatch = useDispatch();
    const { endpoint, retry } = useSelector(selectWebsocket);
    const { onCall, group, room } = useSelector(selectVoiceCall);

    useEffect(() => {
        if (retry <= MAX_RETRY) {
            // 初回は待ち時間なしで接続
            const waittime = retry === 1 ? 0 : Math.pow(2, retry) * 1000;
            setTimeout(() => dispatch(startWebsocket()), waittime);
        } else {
            console.error('websocket connection is closed.');
            dispatch(setTopError(i18next.t('error.internalServerError')));
        }
    }, [dispatch, retry]);

    useEffect(() => {
        if (endpoint !== null) {
            SOCKET = new WebSocket(endpoint);
        }
    }, [endpoint]);

    useEffect(() => {
        if (SOCKET !== null) {
            SOCKET.onmessage = (event: MessageEvent) => {
                const data = JSON.parse(event.data);

                if (!isBasicMessage(data)) {
                    console.log(data);
                    return;
                }

                if (isUpdatedChatGroupRoomStatusMessage(data)) {
                    if (onCall && group !== undefined && room !== undefined) {
                        dispatch(updateMembers());
                        dispatch(updateRooms({ groupId: group.id, roomIds: [room.id] }));
                    } else {
                        // 違う端末で入室中の場合
                        throttledRefetchAllRooms(dispatch);
                    }
                    return;
                }

                if (isUpdateWebChatGroupMessage(data)) {
                    dispatch(
                        updateRooms({ groupId: data.message.chat_group_id, roomIds: [data.message.chat_group_room_id] })
                    );
                    return;
                }

                if (isGroupStatusMessage(data)) {
                    dispatch(updateRooms({ groupId: data.message.group_id, roomIds: data.message.room_ids }));
                    return;
                }
            };

            SOCKET.onclose = () => {
                dispatch(increaseRetry());
            };
        }
    });

    return props.children;
};

type BasicMessage = {
    identifier: {
        channel: string;
    };
    message: any;
};

const isBasicMessage = (x: any): x is BasicMessage => typeof x?.identifier?.channel === 'string';

// trigger when chat_group_room_users is created or destroyed
type UpdatedChatGroupRoomStatusMessage = {
    identifier: {
        channel: 'UpdatedChatGroupRoomStatus';
    };
    message: {
        event: true;
    };
};

const isUpdatedChatGroupRoomStatusMessage = (x: BasicMessage): x is UpdatedChatGroupRoomStatusMessage =>
    x.identifier.channel === 'UpdatedChatGroupRoomStatus';

type UpdateWebChatGroupMessage = {
    identifier: {
        channel: 'UpdateWebChatGroupMessage';
    };
    message: {
        chat_group_id: number;
        chat_group_room_id: number;
    };
};

const isUpdateWebChatGroupMessage = (x: BasicMessage): x is UpdateWebChatGroupMessage =>
    x.identifier.channel === 'UpdateWebChatGroupChannel';

// trigger when group or room is committed
type GroupStatusMessage = {
    identifier: {
        channel: 'GroupStatusChannel';
    };
    message: {
        group_id: number;
        room_ids: number[];
    };
};

const isGroupStatusMessage = (x: BasicMessage): x is GroupStatusMessage =>
    x.identifier.channel === 'GroupStatusChannel';
